import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { AdminCommission } from '../../api/adminTypes'
import type { CommissionByStrategy, CommissionItem } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useEmailParam } from './adminHelpers'
const DEFAULT_EXCHANGE = 'binance'

/**
 * Equivalent of legacy `AdminUserStrategyCommissionComponent` (route
 * `admin/users/strategy/commission` — the one legacy admin page using
 * apexcharts instead of ng2-charts, per the audit). Two contract endpoints:
 *  - #29 `.../commission/month` — real, same `Commission` shape the
 *    user-facing statistics page uses;
 *  - #16 `/admin/strategies/{ex}/commission` — ARCHIVE (409
 *    `email_not_set`/`user_not_found`, else `[]`) — kept alongside the real
 *    figure so an admin sees both the (frozen, empty) legacy history and the
 *    live one.
 */
export function AdminUserStrategyCommissionPage() {
  const [email, setEmail] = useEmailParam()
  const [exchangeId, setExchangeId] = useState(DEFAULT_EXCHANGE)
  const [month, setMonth] = useState('')
  const [commission, setCommission] = useState<AdminCommission | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const [archiveNote, setArchiveNote] = useState<string | null>(null)

  const load = useCallback((targetEmail: string, ex: string, targetMonth: string) => {
    setCommission(null)
    setFeedback(undefined)
    adminClient
      .commissionMonth(targetEmail, targetMonth || undefined)
      .then((result) => setCommission(result))
      .catch((err) => setFeedback(describeAdminError(err)))
    adminClient
      .strategyCommissionArchive(ex, targetEmail)
      .then((rows) => setArchiveNote(rows.length === 0 ? 'empty (expected — ARCHIVE)' : JSON.stringify(rows)))
      .catch((err) => setArchiveNote(describeAdminError(err).text))
  }, [])

  useEffect(() => {
    if (email) load(email, exchangeId, month)
    else {
      setCommission(null)
      setArchiveNote(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const itemColumns: Column<CommissionItem>[] = [
    { key: 'coin2', label: 'Quote asset', align: 'left', sortValue: (r) => r.coin2 },
    { key: 'profit', label: 'Profit', align: 'right', numeric: true, sortValue: (r) => r.profit, format: (_v, r) => formatNumberByCoin(r.profit, r.coin2), cellClass: (v) => (Number(v) < 0 ? 'portal-pnl-negative' : Number(v) > 0 ? 'portal-pnl-positive' : undefined) },
    { key: 'systemCommission', label: 'System commission', align: 'right', numeric: true, sortValue: (r) => r.systemCommission, format: (_v, r) => formatNumberByCoin(r.systemCommission, r.coin2) },
    { key: 'strategyCommission', label: 'Strategy commission', align: 'right', numeric: true, sortValue: (r) => r.strategyCommission, format: (_v, r) => formatNumberByCoin(r.strategyCommission, r.coin2) },
  ]

  function renderByStrategy(row: CommissionByStrategy) {
    return (
      <div key={String(row.strategyId)}>
        <h4>{row.strategyId === null ? 'Manual (no strategy)' : `Strategy ${row.strategyId}`}</h4>
        <div className="portal-table-scroll">
          <Table columns={itemColumns} rows={row.items} rowKey={(r, i) => `${r.coin2}-${i}`} />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user strategy commission</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <form
          className="portal-toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            if (email) load(email, exchangeId, month)
          }}
        >
          <label htmlFor="commission-exchange">Exchange</label>
          <input id="commission-exchange" value={exchangeId} onChange={(e) => setExchangeId(e.target.value)} />
          <label htmlFor="commission-month">Month (YYYYMM, blank = this month)</label>
          <input id="commission-month" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="202609" />
          <button type="submit" disabled={!email}>
            Reload
          </button>
        </form>
        <AdminNotice feedback={feedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to view their commission.</p>}
        {email && commission === null && !feedback && <p>Loading…</p>}
        {email && commission !== null && (
          <>
            <h3>By quote asset</h3>
            <div className="portal-table-scroll">
              <Table columns={itemColumns} rows={commission.all} rowKey={(r, i) => `${r.coin2}-${i}`} />
            </div>
            <h3>By strategy</h3>
            {commission.byStrategy.length === 0 ? <p className="portal-text-muted">None.</p> : commission.byStrategy.map(renderByStrategy)}
          </>
        )}
        {email && archiveNote !== null && (
          <p className="portal-notice portal-notice--info">Legacy per-exchange archive (contract #16): {archiveNote}</p>
        )}
      </div>
    </div>
  )
}
